import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import type { CourseKeyAlertItem, CourseKeyPolicyItem, CourseKeyStepItem } from '@/types/course-key';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';

type StepBuilderProps = {
  label: string;
  description?: string;
  steps: CourseKeyStepItem[];
  onChange: (steps: CourseKeyStepItem[]) => void;
  allowWide?: boolean;
  minBodyHeight?: number;
};

export function CourseKeyStepBuilder({
  label,
  description,
  steps,
  onChange,
  allowWide = false,
  minBodyHeight = 120,
}: StepBuilderProps) {
  const addStep = () => {
    onChange([...steps, { title: '', bodyHtml: '' }]);
  };

  const updateStep = (index: number, patch: Partial<CourseKeyStepItem>) => {
    onChange(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  };

  const removeStep = (index: number) => {
    onChange(steps.filter((_, i) => i !== index));
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= steps.length) return;
    const next = [...steps];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Label>{label}</Label>
          {description ? <p className="mt-1 text-xs text-muted-foreground">{description}</p> : null}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addStep}>
          <Plus className="mr-1 h-4 w-4" />
          Add step
        </Button>
      </div>

      {steps.length === 0 ? (
        <p className="text-xs text-muted-foreground">No steps yet. Add at least one step.</p>
      ) : (
        <div className="space-y-3">
          {steps.map((step, index) => (
            <div key={`step-${index}`} className="rounded-md border bg-muted/20 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Step {index + 1}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={index === 0}
                    onClick={() => moveStep(index, -1)}
                    aria-label="Move step up"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={index === steps.length - 1}
                    onClick={() => moveStep(index, 1)}
                    aria-label="Move step down"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => removeStep(index)}
                    aria-label="Remove step"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Input
                placeholder="Step title"
                value={step.title}
                onChange={(e) => updateStep(index, { title: e.target.value })}
              />
              <RichTextEditor
                value={step.bodyHtml}
                onChange={(v) => updateStep(index, { bodyHtml: v })}
                minHeight={minBodyHeight}
                enableAI={false}
                enableImageUrl
              />
              {allowWide ? (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={Boolean(step.wide)}
                    onCheckedChange={(checked) => updateStep(index, { wide: checked === true })}
                  />
                  Full-width step (spans both columns on student view)
                </label>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type AlertBuilderProps = {
  label: string;
  alerts: CourseKeyAlertItem[];
  onChange: (alerts: CourseKeyAlertItem[]) => void;
};

export function CourseKeyAlertBuilder({ label, alerts, onChange }: AlertBuilderProps) {
  const addAlert = () => {
    onChange([...alerts, { title: '', bodyHtml: '', variant: 'info' }]);
  };

  const updateAlert = (index: number, patch: Partial<CourseKeyAlertItem>) => {
    onChange(alerts.map((alert, i) => (i === index ? { ...alert, ...patch } : alert)));
  };

  const removeAlert = (index: number) => {
    onChange(alerts.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <Button type="button" variant="outline" size="sm" onClick={addAlert}>
          <Plus className="mr-1 h-4 w-4" />
          Add notice
        </Button>
      </div>
      {alerts.length === 0 ? (
        <p className="text-xs text-muted-foreground">Optional callout boxes shown below the steps.</p>
      ) : (
        alerts.map((alert, index) => (
          <div key={`alert-${index}`} className="space-y-2 rounded-md border bg-muted/20 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Notice {index + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive"
                onClick={() => removeAlert(index)}
                aria-label="Remove notice"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Input
              placeholder="Notice title"
              value={alert.title}
              onChange={(e) => updateAlert(index, { title: e.target.value })}
            />
            <Select
              value={alert.variant}
              onValueChange={(v) => updateAlert(index, { variant: v as CourseKeyAlertItem['variant'] })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Style" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning (highlighted)</SelectItem>
              </SelectContent>
            </Select>
            <RichTextEditor
              value={alert.bodyHtml}
              onChange={(v) => updateAlert(index, { bodyHtml: v })}
              minHeight={100}
              enableAI={false}
              enableImageUrl
            />
          </div>
        ))
      )}
    </div>
  );
}

type PolicyBuilderProps = {
  label: string;
  policies: CourseKeyPolicyItem[];
  onChange: (policies: CourseKeyPolicyItem[]) => void;
};

export function CourseKeyPolicyBuilder({ label, policies, onChange }: PolicyBuilderProps) {
  const addPolicy = () => {
    onChange([...policies, { title: '', bodyHtml: '', variant: 'default' }]);
  };

  const updatePolicy = (index: number, patch: Partial<CourseKeyPolicyItem>) => {
    onChange(policies.map((policy, i) => (i === index ? { ...policy, ...patch } : policy)));
  };

  const removePolicy = (index: number) => {
    onChange(policies.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <Button type="button" variant="outline" size="sm" onClick={addPolicy}>
          <Plus className="mr-1 h-4 w-4" />
          Add policy
        </Button>
      </div>
      {policies.length === 0 ? (
        <p className="text-xs text-muted-foreground">No policies yet.</p>
      ) : (
        policies.map((policy, index) => (
          <div key={`policy-${index}`} className="space-y-2 rounded-md border bg-muted/20 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Policy {index + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive"
                onClick={() => removePolicy(index)}
                aria-label="Remove policy"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Input
              placeholder="Policy title"
              value={policy.title}
              onChange={(e) => updatePolicy(index, { title: e.target.value })}
            />
            <Select
              value={policy.variant}
              onValueChange={(v) =>
                updatePolicy(index, { variant: v as CourseKeyPolicyItem['variant'] })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Style" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="warning">Warning (highlighted)</SelectItem>
              </SelectContent>
            </Select>
            <RichTextEditor
              value={policy.bodyHtml}
              onChange={(v) => updatePolicy(index, { bodyHtml: v })}
              minHeight={100}
              enableAI={false}
              enableImageUrl
            />
          </div>
        ))
      )}
    </div>
  );
}
